import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { compact, isNil, isNumber, keyBy, pick, sortBy } from 'lodash'
import UserAction from '../../../administration/components/user/actions/UserAction'
import StationAction from '../../../station/actions/StationAction'
import { getStationFilterByModule } from '../../../utils/StationUtils'
import { getUserBookmarksByStationType } from '../../../utils/UserUtil'
import AdministrationAction from 'administration/actions/AdministrationAction'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import SuperMultiAutocomplete from '../SuperMultiAutocomplete'
import FilterDialog from 'installation/components/installations/components/modal/FilterDialog'
import useUpdateEffect from 'utils/customHook/useUpdateEffect'
import { FILTER_TYPE } from 'administration/components/user/constants/UserConstants'

const SimpleFilterSelect = ({
    col,
    onChange = () => { },
    stationType = '',
    disabled = false,
    title,
    stations = [],
    value,
    hidden,
    isValidateOpen = false,
    setIsOpenValidate = () => { },
    launchSearch = () => { },
    'data-cy': datacy,
    ignoreCache = false,
}) => {
    const dispatch = useDispatch()

    const {
        filters,
        userBookmarks,
    } = useSelector(store => ({
        filters: store.StationReducer.filters,
        userBookmarks: store.UserReducer.userBookmarks,
    }), shallowEqual)

    const [filter, setFilter] = useState(() => {
        if (ignoreCache) {
            return isNumber(value) ? value : -1
        }
        return (isNumber(value) ? value : pick(AdministrationAction.getCache(stationType), ['filter']).filter) ?? -1
    })
    const [filterResults, setFilterResults] = useState()
    const [parameters, setParameters] = useState(() => {
        if (ignoreCache) {
            return []
        }
        return pick(AdministrationAction.getCache(stationType), ['parameters']).parameters || []
    })
    const isFirstRender = useRef(true)

    useUpdateEffect(() => {
        setFilter(value)
    }, [value])

    const options = useMemo(() => {
        const filterOptions = sortBy(getStationFilterByModule(filters, stationType).map(f => ({ value: f.code, label: f.name })), 'label')
        return [{ value: FILTER_TYPE.BOOKMARK, label: i18n.bookmarks }, ...filterOptions]
    }, [filters, stationType])

    const getStationResults = (resultsFilter = []) => {
        if (filter === FILTER_TYPE.NOFILTER) { // pas de filtre
            return []
        }
        if (filter === FILTER_TYPE.BOOKMARK) { // filtre "favoris"
            return compact(resultsFilter.map(b => stations.find(q => q.code?.toUpperCase() === b)))
        }
        return stations.filter(s => resultsFilter.includes(s.id))
    }

    const onChangeFilter = (newValue) => {
        setFilter(newValue ?? -1)
        if (isNil(newValue)) {
            setParameters([])
        } else {
            const filtersByModules = getStationFilterByModule(filters, stationType)
            const newFilter = filtersByModules.find(f => f.code == newValue)
            setParameters(newFilter?.link_parameters)
        }
    }

    useEffect(() => {
        if (isFirstRender.current && filterResults?.length) {
            isFirstRender.current = false
            launchSearch(getStationResults(filterResults), filter, parameters)
        } else if (isFirstRender.current && filter === FILTER_TYPE.NOFILTER) {
            isFirstRender.current = false
        }
    }, [filterResults, filter, parameters])

    useEffect(() => {
        dispatch(UserAction.fetchBookmarks())
        dispatch(StationAction.fetchFilters())
    }, [])

    const foundFilter = useMemo(() => {
        const filtersByModules = getStationFilterByModule(filters, stationType)
        return filtersByModules.find(f => f.code == filter)
    }, [filter, filters, stationType])

    useEffect(() => {
        if (!ignoreCache) {
            dispatch(AdministrationAction.setCache(stationType, { filter, parameters }))
        }
        if (filter === FILTER_TYPE.NOFILTER) {
            setFilterResults([])
        } else if (filter === FILTER_TYPE.BOOKMARK) {
            const codes = getUserBookmarksByStationType(userBookmarks, stationType).map(c => c.toUpperCase())
            setFilterResults(codes)
        } else if (foundFilter) {
            dispatch(StationAction.fetchFilterResults({ ...foundFilter, parameters }))
                .then((resultsFilter = []) => {
                    setFilterResults(resultsFilter.map(res => res.id))
                })
        } else {
            setFilterResults([])
        }
    }, [filter, filters, foundFilter, parameters])

    useEffect(() => {
        if (filterResults) {
            const results = getStationResults(filterResults)
            onChange(results, filter, parameters)
        }
    }, [userBookmarks, stationType, filterResults, stations.length]) // needed .length cause on some component the list change every rerender so this useEffect is reexecute and it update the parent rerender

    useEffect(() => {
        launchSearch(getStationResults(filterResults), filter, parameters)
    }, [])

    const nbStations = useMemo(() => {
        if (filter === FILTER_TYPE.NOFILTER) { // pas de filtre
            return 0
        }
        if (filter === FILTER_TYPE.BOOKMARK) { // filtre "favoris"
            const keyIndexStations = keyBy(stations, s => s.code?.toUpperCase())
            return (filterResults || []).map(code => keyIndexStations[code]).filter(s => !!s).length
        }
        const keyIndexStations = keyBy(stations, 'id')
        return (filterResults || []).map(id => keyIndexStations[id]).filter(s => !!s).length
    }, [filterResults, stations.length])

    if (hidden) {
        return null
    }

    return (
        <>
            <SuperMultiAutocomplete
                col={col}
                options={options}
                values={filter !== FILTER_TYPE.NOFILTER ? filter : undefined}
                label={`${title || i18n.filter} ${filter !== FILTER_TYPE.NOFILTER ? `(${nbStations} ${nbStations > 1 ? i18n.stations : i18n.station})` : ''}`}
                keyLabel='label'
                keyValue='value'
                onChange={onChangeFilter}
                disabled={disabled}
                data-cy={datacy}
            />
            {isValidateOpen && (
                <FilterDialog
                    parameters={parameters}
                    isOpen={isValidateOpen}
                    setIsOpen={setIsOpenValidate}
                    launchSearch={newParameters => {
                        dispatch(StationAction.fetchFilterResults({ ...foundFilter, parameters: newParameters }))
                            .then((resultsFilter = []) => {
                                const results = resultsFilter.map(res => res.id)
                                setFilterResults(results)
                                setParameters(newParameters)
                                setIsOpenValidate(false)
                                if (!ignoreCache) {
                                    dispatch(AdministrationAction.setCache(stationType, { parameters: newParameters }))
                                }
                                launchSearch(getStationResults(results), filter, newParameters)
                            })
                    }}
                />
            )}
        </>
    )
}

SimpleFilterSelect.propTypes = {
    /**
     * @deprecated remove all row / col and use Grid instead
     */
    col: PropTypes.number,
    onChange: PropTypes.func,
    stationType: PropTypes.oneOf([
        STATION_TYPE_NAME.quality,
        STATION_TYPE_NAME.piezometry,
        STATION_TYPE_NAME.pluviometry,
        STATION_TYPE_NAME.hydrometry,
        STATION_TYPE_NAME.productionUnit,
        STATION_TYPE_NAME.distributionUnit,
        STATION_TYPE_NAME.installation,
        STATION_TYPE_NAME.resource,
        STATION_TYPE_NAME.catchment,
    ]),
    disabled: PropTypes.bool,
    title: PropTypes.string,
    stations: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
    })),
    value: PropTypes.number,
    hidden: PropTypes.bool,
    isValidateOpen: PropTypes.func,
    setIsOpenValidate: PropTypes.func,
    launchSearch: PropTypes.func,
    'data-cy': PropTypes.string,
    ignoreCache: PropTypes.bool,
}

export default SimpleFilterSelect